import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import StageFieldsForm, { missingFields } from "@/components/pipeline/StageFieldsForm";
import { toast } from "sonner";
import { XCircle } from "lucide-react";

function LostDialog({ open, onOpenChange, deal, outcomes, onDone }) {
  const [lost, setLost] = useState({ outcome: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!lost.outcome) return toast.error("Select an outcome");
    setBusy(true);
    try {
      await api.post(`/deals/${deal.id}/outcome`, lost);
      toast.success("Deal marked as lost");
      setLost({ outcome: "", reason: "" });
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="lost-dialog">
        <DialogHeader><DialogTitle>Mark deal as lost</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Outcome</Label>
            <Select value={lost.outcome} onValueChange={(v) => setLost({ ...lost, outcome: v })}>
              <SelectTrigger className="mt-1.5" data-testid="lost-outcome-select"><SelectValue placeholder="Select reason" /></SelectTrigger>
              <SelectContent>{outcomes.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Notes</Label><Textarea className="mt-1.5" rows={2} value={lost.reason} onChange={(e) => setLost({ ...lost, reason: e.target.value })} data-testid="lost-notes-input" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={save} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="save-lost-btn">Confirm</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Tap-to-edit alternative to drag & drop (works well on mobile). */
export default function DealDialog({ deal, stages, stageFields, outcomes, canEdit, onClose, onMove, onChanged }) {
  const [stage, setStage] = useState("");
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);

  useEffect(() => { if (deal) { setStage(deal.stage); setValues({ ...(deal.stage_fields || {}) }); } }, [deal]);

  const fields = stageFields[stage] || [];
  const save = async () => {
    const missing = missingFields(fields, values);
    if (missing.length) return toast.error(`Please fill: ${missing.map((f) => f.label).join(", ")}`);
    setBusy(true);
    const payload = Object.fromEntries(fields.map((f) => [f.key, values[f.key]]).filter(([, v]) => v != null && v !== ""));
    const ok = await onMove(deal, deal.stage, stage, payload);
    setBusy(false);
    if (ok) onClose();
  };

  return (
    <>
      <Dialog open={!!deal && !lostOpen} onOpenChange={(o) => !o && onClose()}>
        <DialogContent data-testid="deal-dialog">
          <DialogHeader>
            <DialogTitle>{deal?.title}</DialogTitle>
            <DialogDescription>{canEdit ? "Update the stage. Some stages require extra details." : "You have view-only access to this deal."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Stage</Label>
              <Select value={stage} onValueChange={setStage} disabled={!canEdit}>
                <SelectTrigger className="mt-1.5" data-testid="deal-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>{stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {canEdit && <StageFieldsForm stage={stage} fields={fields} values={values} onChange={setValues} highlightMissing />}
          </div>
          {canEdit && (
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setLostOpen(true)} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="mark-lost-btn"><XCircle className="h-4 w-4" /> Mark Lost</Button>
              <div className="flex gap-2 sm:ml-auto">
                <Button variant="outline" onClick={onClose}>Cancel</Button>
                <Button onClick={save} disabled={busy || stage === deal?.stage && !fields.length} className="bg-teal hover:bg-teal-700" data-testid="save-stage-btn">Save stage</Button>
              </div>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
      {deal && <LostDialog open={lostOpen} onOpenChange={setLostOpen} deal={deal} outcomes={outcomes} onDone={() => { setLostOpen(false); onClose(); onChanged(); }} />}
    </>
  );
}
